
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { saveCurrentCampaign, collectCampaignData } from './services/campaignSaveService';
import { showInfo, showSuccess, showError } from '@/hooks/use-toast';
import { sendCampaignConfirmationEmail } from './services/payment/emailService';

export const usePaymentSubmission = (
  setShowPayment: (show: boolean) => void, 
  setShowThankYou: (show: boolean) => void,
  currentCampaignId: string | null,
  setCurrentCampaignId: (id: string | null) => void
) => {
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { user } = useAuth();

  /**
   * Handle payment completion - this is the ONLY place campaigns are saved
   */
  const handlePaymentComplete = async () => {
    // Prevent multiple submissions
    if (hasSubmitted) {
      console.log('[PaymentSubmission] Already submitted, showing thank you');
      setShowThankYou(true);
      return;
    }
    
    setIsCreatingCampaign(true);
    setHasSubmitted(true);
    
    try {
      console.log('[PaymentSubmission] Starting payment completion');
      
      // Generate campaign ID if needed
      const isValidUuid = (id: string | null | undefined) =>
        typeof id === 'string' &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);

      let campaignId = currentCampaignId;

      if (!isValidUuid(campaignId)) {
        const stored = localStorage.getItem('currentCampaignId');
        campaignId = isValidUuid(stored) ? stored : crypto.randomUUID();
        console.log('[PaymentSubmission] Generated campaign ID:', campaignId);
      }
      
      // Update state
      setCurrentCampaignId(campaignId);
      localStorage.setItem('currentCampaignId', campaignId);
      
      showInfo({
        title: "Processing",
        description: "Creating your campaign...",
      });
      
      // Collect all campaign data first
      const campaignData = collectCampaignData({
        campaignId,
        userId: user?.id,
        creatorEmail: user?.email,
        status: 'pending'
      });
      
      // SAVE THE CAMPAIGN - single point of saving
      const saveResult = await saveCurrentCampaign({
        campaignId,
        userId: user?.id,
        creatorEmail: user?.email,
        status: 'pending'
      });
      
      if (saveResult.success) {
        console.log('[PaymentSubmission] Campaign saved successfully:', saveResult.campaignId);
        
        // Send confirmation email with proper arguments
        try {
          await sendCampaignConfirmationEmail(
            campaignId,
            { 
              email: campaignData.email,
              companyName: campaignData.companyName,
              brandName: campaignData.brandName,
              personInCharge: campaignData.personInCharge,
              jobTitle: campaignData.jobTitle,
              mobile: campaignData.mobile
            },
            campaignData.selectedLocations as any,
            campaignData.totalDevices,
            campaignData.startDate,
            campaignData.endDate,
            campaignData.durationDays,
            campaignData.budgetInfo
          );
          console.log('[PaymentSubmission] Confirmation email sent');
        } catch (emailError) {
          console.warn('[PaymentSubmission] Email failed (non-critical):', emailError);
        }
        
        showSuccess({
          title: "Success",
          description: "Campaign created successfully!",
        });
      } else {
        console.error('[PaymentSubmission] Save failed:', saveResult.error);
        showError({
          title: "Warning",
          description: "Campaign submitted but there was an issue saving. Please contact support.",
        });
      }
      
      // Show thank you regardless
      setShowPayment(false);
      setShowThankYou(true);
      localStorage.setItem('showThankYou', 'true');
      
      // Clear campaign ID after successful save
      localStorage.removeItem('currentCampaignId');
      
    } catch (error) {
      console.error('[PaymentSubmission] Error:', error);
      showError({
        title: "Error",
        description: "An error occurred. Your campaign may still have been saved.",
      });
      
      // Still show thank you
      setShowPayment(false);
      setShowThankYou(true);
    } finally {
      setIsCreatingCampaign(false);
    }
  };

  return {
    isCreatingCampaign,
    hasSubmitted,
    handlePaymentComplete
  };
};
